import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  resource,
  signal,
} from '@angular/core';

import { SearchInputComponent } from '@core/components/search-input/search-input.component';
import { SelectComponent } from '@core/components/select/select.component';
import { SocketService } from '@core/services/socket.service';
import { ColumnDef, PaginationState } from '@tanstack/angular-table';
import { TableComponent } from '@core/components/table/table.component';
import { ClipboardService } from '@core/services/clipboard.service';
import { LoadingIconComponent } from '@core/components/loading-icon/loading-icon.component';
import { TablePaginationComponent } from '@core/components/table/table-pagination/table-pagination.component';
import { AdminAffiliateStat } from '../admin-affiliate-stat.interface';
import { AdminAffiliateTrackerEvent } from './admin-affiliate-tracker.interface';
import { adminAffiliateTrackerTableConfig } from './admin-affiliate-tracker-modal.config';

interface TypeOption {
  label: string;
  value: string;
  fn: () => void;
}

interface OrderOption {
  label: string;
  key: string;
  fn: () => void;
}

interface AppliedFilters {
  userid: string;
  name: string;
  source: string;
  source_id: string;
}

@Component({
  selector: 'app-admin-affiliate-tracker-modal',
  templateUrl: './admin-affiliate-tracker-modal.component.html',
  styleUrl: './admin-affiliate-tracker-modal.component.scss',
  standalone: true,
  imports: [
    SelectComponent,
    SearchInputComponent,
    TableComponent,
    LoadingIconComponent,
    TablePaginationComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'modal-container flex flex-col',
  },
})
export class AdminAffiliateTrackerModalComponent {
  private socketService = inject(SocketService);
  private clipboardService = inject(ClipboardService);

  affiliate = input.required<AdminAffiliateStat>();
  range = input.required<'all' | 'day' | 'week' | 'month'>();

  readonly columns: ColumnDef<AdminAffiliateTrackerEvent>[] =
    adminAffiliateTrackerTableConfig({
      copy: this.clipboardService.copy.bind(this.clipboardService),
    });

  // draft filter values, applied on Enter / Apply
  readonly userIdFilter = signal<string>('');
  readonly nameFilter = signal<string>('');
  readonly sourceFilter = signal<string>('');
  readonly sourceIdFilter = signal<string>('');

  readonly appliedFilters = signal<AppliedFilters>({
    userid: '',
    name: '',
    source: '',
    source_id: '',
  });

  readonly totalPages = signal(1);

  readonly pagination = signal<PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });

  readonly typeOptions: TypeOption[] = [
    {
      label: 'ALL TYPES',
      value: '',
      fn: () => {
        this.type.set(this.typeOptions[0]);
        this.resetPage();
      },
    },
    {
      label: 'DEPOSIT',
      value: 'deposit',
      fn: () => {
        this.type.set(this.typeOptions[1]);
        this.resetPage();
      },
    },
    {
      label: 'RAKE',
      value: 'rake',
      fn: () => {
        this.type.set(this.typeOptions[2]);
        this.resetPage();
      },
    },
    {
      label: 'WAGER',
      value: 'wager',
      fn: () => {
        this.type.set(this.typeOptions[3]);
        this.resetPage();
      },
    },
  ];
  readonly type = signal<TypeOption>(this.typeOptions[0]);

  // DESC first: it is the default when the modal opens.
  readonly orderOptions: OrderOption[] = [
    {
      label: 'DESC',
      key: 'desc',
      fn: () => {
        this.order.set(this.orderOptions[0]);
      },
    },
    {
      label: 'ASC',
      key: 'asc',
      fn: () => {
        this.order.set(this.orderOptions[1]);
      },
    },
  ];
  readonly order = signal<OrderOption>(this.orderOptions[0]);

  readonly orderFieldOptions: OrderOption[] = [
    {
      label: 'DEFAULT',
      key: 'default',
      fn: () => {
        this.orderField.set(this.orderFieldOptions[0]);
      },
    },
    {
      label: 'TIME',
      key: 'time_stamp',
      fn: () => {
        this.orderField.set(this.orderFieldOptions[1]);
      },
    },
    {
      label: 'EARNED',
      key: 'earned',
      fn: () => {
        this.orderField.set(this.orderFieldOptions[2]);
      },
    },
    {
      label: 'AMOUNT',
      key: 'amount',
      fn: () => {
        this.orderField.set(this.orderFieldOptions[3]);
      },
    },
  ];
  readonly orderField = signal<OrderOption>(this.orderFieldOptions[0]);

  eventsResource = resource({
    params: () => {
      return {
        ref_by: this.affiliate().userid,
        range: this.range(),
        page: this.pagination().pageIndex + 1,
        order: this.orderField().key,
        order_by: this.order().key,
        type: this.type().value,
        filters: this.appliedFilters(),
      };
    },
    loader: async (params) => {
      const { ref_by, range, page, order, order_by, type, filters } =
        params.params;

      const payload: Record<string, unknown> = {
        ref_by,
        range,
        page,
        order,
        order_by,
      };
      if (type) payload['type'] = type;
      if (filters.userid) payload['userid'] = filters.userid;
      if (filters.name) payload['name'] = filters.name;
      if (filters.source) payload['source'] = filters.source;
      if (filters.source_id) payload['source_id'] = filters.source_id;

      const response = await this.socketService.emit(
        'admin_load_affiliate_tracker',
        payload
      );
      // response is { ref_by, range, page, pages, total, total_amount, total_earned, events: [...] }
      this.totalPages.set(response?.pages ?? 1);
      return (response?.events ?? []) as AdminAffiliateTrackerEvent[];
    },
  });

  public applyFilters() {
    this.resetPage();
    this.appliedFilters.set({
      userid: this.userIdFilter().trim(),
      name: this.nameFilter().trim(),
      source: this.sourceFilter().trim(),
      source_id: this.sourceIdFilter().trim(),
    });
  }

  public handleResetFilters() {
    this.userIdFilter.set('');
    this.nameFilter.set('');
    this.sourceFilter.set('');
    this.sourceIdFilter.set('');
    this.type.set(this.typeOptions[0]);
    this.applyFilters();
  }

  public handlePaginationChange(action: 'prev' | 'next' | 'reset') {
    this.pagination.update((state) => {
      return {
        ...state,
        pageIndex:
          action === 'reset'
            ? 0
            : action === 'prev'
            ? state.pageIndex - 1
            : state.pageIndex + 1,
      };
    });
  }

  private resetPage() {
    this.pagination.update((state) => ({ ...state, pageIndex: 0 }));
  }
}
